import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { AbstractListViewService } from '@examdojo/core/list-view';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { TranslocoPipe } from '@jsverse/transloco';
import { GridApi, SortDirection } from 'ag-grid-community';
import { combineLatest, filter, tap } from 'rxjs';
import { AbstractSimpleAgGridService } from '../list-view-ag-grid.service';

interface SortOption {
  label: string;
  sort: SortDirection;
  colId: string;
  initialSort: boolean;
}
@Component({
  selector: 'dojo-list-view-external-sorting',
  template: `
    @if (sortOptions().length) {
      <dojo-button [matMenuTriggerFor]="menu" [fill]="'outline'" class="block w-full md:w-auto">
        <dojo-icon [icon]="'bars-sort'" class="mr-2 text-neutral-300"></dojo-icon>
        <span>
          {{
            formCtrl.value && sortOptionsMap()[formCtrl.value]
              ? sortOptionsMap()[formCtrl.value].label
              : ('assessment.popup.sorting' | transloco)
          }}</span
        >
      </dojo-button>

      <mat-menu #menu="matMenu" class="custom-mat-menu">
        <div mat-menu-item class="section-title">
          <span>{{ 'assessment.popup.sort_by' | transloco }}</span>
        </div>
        @for (option of sortOptions(); track $index) {
          <button
            mat-menu-item
            (click)="selectSortOption(option)"
            [class.active]="formCtrl.value === option.colId + option.sort"
          >
            {{ option.label }}
          </button>
        }
      </mat-menu>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, ButtonComponent, MatMenu, MatMenuTrigger, MatMenuItem, IconComponent, TranslocoPipe],
})
export class ListViewExternalSortingComponent {
  constructor(
    private readonly listViewAgGridService: AbstractSimpleAgGridService,
    private readonly listViewService: AbstractListViewService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    combineLatest([this.listViewService.gridApi$.pipe(filter(isNotNullish)), this.formCtrl.valueChanges])
      .pipe(
        tap(([, value]) => {
          if (!value) {
            return;
          }

          const sortOption = this.sortOptionsMap()[value];

          if (!sortOption) {
            return;
          }

          this.applySort(sortOption.colId, sortOption.sort);
        }),

        takeUntilDestroyed(),
      )
      .subscribe();

    // Listen to ag-grid sort changes and sync with external sorting dropdown
    this.listViewService.gridApi$
      .pipe(
        filter(isNotNullish),
        tap((gridApi) => {
          gridApi.addEventListener('sortChanged', () => {
            this.syncExternalSortWithGridSort(gridApi);
          });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.sortOptionsMap.set(this.convertExternalSortColumnDefitionsToSortOptions());
    this.setInitialSort();
  }

  readonly formCtrl = new FormControl<string | null>(null);

  readonly sortOptionsMap = signal<Record<string, SortOption>>({});
  readonly sortOptions = computed(() => Object.values(this.sortOptionsMap()));

  selectSortOption(sortOption: SortOption) {
    this.formCtrl.setValue(sortOption.colId + sortOption.sort);
  }

  private syncExternalSortWithGridSort(gridApi: GridApi) {
    const columnState = gridApi.getColumnState();
    const sortedColumn = columnState.find((col) => col.sort !== null && col.sort !== undefined);

    if (!sortedColumn) {
      // No sorting applied, clear the dropdown
      this.formCtrl.setValue(null, { emitEvent: false });
      this.cdr.markForCheck(); // Trigger change detection
      return;
    }

    // Find matching external sort option
    const sortKey = sortedColumn.colId + sortedColumn.sort;
    const sortOptions = this.sortOptionsMap();

    if (sortOptions[sortKey]) {
      // Update dropdown to match current grid sort, but don't emit event to avoid loops
      this.formCtrl.setValue(sortKey, { emitEvent: false });
      this.cdr.markForCheck(); // Trigger change detection
    }
  }

  private applySort(colId: string, sort: SortDirection) {
    const rowGroupSortingStates = this.listViewService.gridApi?.getColumnState().map((columnState) => {
      if (columnState.colId === colId) {
        return {
          ...columnState,
          sort: sort,
          sortIndex: 0,
        };
      } else if (columnState.rowGroup === true || columnState.colId === 'ag-Grid-AutoColumn') {
        return columnState;
      } else {
        return {
          ...columnState,
          sort: null,
        };
      }
    });
    this.listViewService.gridApi?.applyColumnState({
      state: rowGroupSortingStates,
      applyOrder: true,
      defaultState: {
        sort: null,
      },
    });
  }

  private convertExternalSortColumnDefitionsToSortOptions(): Record<string, SortOption> {
    return this.listViewAgGridService.columnDefs
      .flatMap((columnDef) => {
        const externalSortingOrder = columnDef.externalSortingOrder;
        if (!externalSortingOrder || !externalSortingOrder.length) {
          return null;
        }

        const colId = columnDef.colId;
        if (!colId) {
          // colId should be defined if externalSortingOrder is defined. TODO: dev log error
          return null;
        }

        return externalSortingOrder.map((sortOption) => ({
          label: sortOption.label,
          sort: sortOption.sort,
          initialSort: !!sortOption.initial,
          colId,
        }));
      })
      .filter(isNotNullish)
      .reduce(
        (acc, val) => {
          acc[val.colId + val.sort] = val;

          return acc;
        },
        {} as Record<string, SortOption>,
      );
  }

  private setInitialSort() {
    const initialSort = this.sortOptions().find((sortOption) => sortOption.initialSort);

    if (initialSort) {
      this.formCtrl.setValue(initialSort.colId + initialSort.sort, {
        emitEvent: false,
      });
    }
  }
}
